import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, Injectable, PLATFORM_ID, inject } from '@angular/core';

import { ScriptLoaderService } from '@stair-craft/common/util';

import { ITurnstileApi } from '../feedback-form/feedback-form.types';

const TURNSTILE_SCRIPT_ID: string = 'sc-turnstile-script';
const TURNSTILE_LOAD_CALLBACK: string = 'scOnTurnstileLoad';
const RU_LOCALES: ReadonlySet<string> = new Set<string>(['ru', 'be']);

interface IGlobalTurnstileBridge {
    turnstile?: ITurnstileApi;
    [TURNSTILE_LOAD_CALLBACK]?: () => void;
}

/**
 * Everything a form must supply to drive a Turnstile widget. The token sink and
 * the two optional hooks are the only form-specific behaviour; the load / render /
 * reset lifecycle is identical and lives inside the service.
 */
export interface ITurnstileWidgetConfig {
    /** The element the widget renders into. Updated on {@link TurnstileWidgetService.rerender}. */
    container: HTMLElement;
    readonly siteKey: string;
    /** Angular `LOCALE_ID`; mapped to the widget `language` (ru/be → ru, else en). */
    readonly localeId: string;
    /** Called with the verification token, and with `''` on expiry / error / reset. */
    readonly onToken: (token: string) => void;
    /** Called once the widget has rendered (feedback form rescales it to its container). */
    readonly onRendered?: (container: HTMLElement) => void;
    /** Called if the widget has not rendered within {@link loadTimeoutMs} (contact form falls back to a checkbox). */
    readonly onLoadTimeout?: () => void;
    /** Budget after which {@link onLoadTimeout} fires. Omit to disable the timeout. */
    readonly loadTimeoutMs?: number;
}

/**
 * Deep module owning the Cloudflare Turnstile widget lifecycle shared by the
 * feedback and contact forms: deferred script load on viewport intersection,
 * explicit render, token plumbing, locale mapping, and reset. Provide it per
 * form component (`providers: [TurnstileWidgetService]`).
 */
@Injectable()
export class TurnstileWidgetService {
    readonly #platformId: object = inject(PLATFORM_ID);
    readonly #scriptLoader: ScriptLoaderService = inject(ScriptLoaderService);
    readonly #destroyRef: DestroyRef = inject(DestroyRef);

    #config: ITurnstileWidgetConfig | null = null;
    #widgetId: string | null = null;
    #visibilityObserver: IntersectionObserver | null = null;
    #requested: boolean = false;
    #timeoutHandle: ReturnType<typeof setTimeout> | null = null;

    constructor() {
        this.#destroyRef.onDestroy((): void => {
            this.#visibilityObserver?.disconnect();
            if (this.#timeoutHandle !== null) {
                clearTimeout(this.#timeoutHandle);
            }
        });
    }

    /** `true` once `turnstile.render()` has returned a widget id. */
    public get isRendered(): boolean {
        return this.#widgetId !== null;
    }

    /** `true` once script loading has been initiated (survives re-renders). */
    public get hasRequested(): boolean {
        return this.#requested;
    }

    /**
     * Defer loading `api.js` until the captcha container scrolls near the
     * viewport, then render. Call from `afterNextRender` so the container exists.
     * On SSR it is a no-op; without `IntersectionObserver` it loads immediately.
     */
    public scheduleLoad(config: ITurnstileWidgetConfig): void {
        this.#config = config;

        if (!isPlatformBrowser(this.#platformId)) {
            return;
        }

        if (typeof IntersectionObserver === 'undefined') {
            this.#loadScript();
            return;
        }

        this.#visibilityObserver = new IntersectionObserver(
            (entries: IntersectionObserverEntry[]): void => {
                if (entries.some((entry: IntersectionObserverEntry) => entry.isIntersecting)) {
                    this.#visibilityObserver?.disconnect();
                    this.#visibilityObserver = null;
                    this.#loadScript();
                }
            },
            { rootMargin: '200px' }
        );
        this.#visibilityObserver.observe(config.container);
    }

    /**
     * Render into a (possibly new) container — used when a form swaps the
     * captcha container after a successful submit. Pair with {@link forgetWidget}.
     */
    public rerender(container: HTMLElement): void {
        if (this.#config) {
            this.#config.container = container;
        }
        this.#renderWidget();
    }

    /**
     * Drop the reference to the current widget (its DOM container has been
     * destroyed) so the next {@link rerender} produces a fresh one.
     */
    public forgetWidget(): void {
        this.#widgetId = null;
    }

    /** Reset the widget back to unverified and clear the token. */
    public reset(): void {
        const turnstile: ITurnstileApi | undefined = this.#bridge.turnstile;
        if (turnstile && this.#widgetId !== null) {
            turnstile.reset(this.#widgetId);
        }
        this.#config?.onToken('');
    }

    get #bridge(): IGlobalTurnstileBridge {
        return globalThis as unknown as IGlobalTurnstileBridge;
    }

    #loadScript(): void {
        if (this.#requested) {
            return;
        }
        this.#requested = true;

        if (this.#bridge.turnstile) {
            this.#renderWidget();
            return;
        }

        this.#bridge[TURNSTILE_LOAD_CALLBACK] = (): void => {
            this.#renderWidget();
        };

        const config: ITurnstileWidgetConfig | null = this.#config;
        if (config?.loadTimeoutMs !== undefined && config.onLoadTimeout) {
            this.#timeoutHandle = setTimeout((): void => {
                if (this.#widgetId === null) {
                    config.onLoadTimeout?.();
                }
            }, config.loadTimeoutMs);
        }

        void this.#scriptLoader.load({
            id: TURNSTILE_SCRIPT_ID,
            src: `https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=${TURNSTILE_LOAD_CALLBACK}`,
            async: true,
            defer: true,
        });
    }

    #renderWidget(): void {
        const config: ITurnstileWidgetConfig | null = this.#config;
        const turnstile: ITurnstileApi | undefined = this.#bridge.turnstile;
        if (!config || !turnstile || this.#widgetId !== null) {
            return;
        }
        const container: HTMLElement = config.container;

        const widgetId: string | undefined = turnstile.render(container, {
            sitekey: config.siteKey,
            // The site is dark everywhere, so the widget is drawn dark too, not left to the OS setting
            theme: 'dark',
            language: this.#resolveLocale(config.localeId),
            callback: (token: string): void => {
                config.onToken(token);
            },
            'expired-callback': (): void => {
                config.onToken('');
            },
            'error-callback': (): void => {
                config.onToken('');
            },
        });
        this.#widgetId = widgetId ?? null;

        if (this.#widgetId !== null && this.#timeoutHandle !== null) {
            clearTimeout(this.#timeoutHandle);
            this.#timeoutHandle = null;
        }

        // Unconditional, mirroring the feedback form's original render: the width
        // rescale is a no-op when the widget did not render (no child element yet).
        config.onRendered?.(container);
    }

    #resolveLocale(localeId: string): string {
        const primary: string = localeId.split('-')[0].toLowerCase();
        return RU_LOCALES.has(primary) ? 'ru' : 'en';
    }
}
